import AlgoliaSetup from '../algolia/setup';
import instantsearch from 'instantsearch.js';
import configureWidget from '@/algolia/widgets/configure.js';
import searchWidget from '@/algolia/widgets/search.js';

// import clearWidget from '@/algolia/widgets/clear.js';
import searchResultsWidget from '@/algolia/widgets/searchResults.js';
import { refinementList } from 'instantsearch.js/es/widgets';
import { eventBus, withPlugins, domEvents, device } from '@spon/plugins';

const AlgoliaSearch = async ({ node, plugins: { addEvents, device } }) => {
	const {
		searchClient,
		index,
		config,
		user: { status: hasActiveSubscription },
	} = await AlgoliaSetup(node);
	const urlParams = new URLSearchParams(window.location.search);
	const startingQuery = urlParams.get('query');
	const pageUrl = window.location.origin + window.location.pathname;

	const placeholders = [
		'Search by artwork title, context or artform',
		'Search by keyword',
	];

	// Used to compare against expiry date / (opportuity) deadlines
	// const nowStamp = Math.floor(new Date().getTime() / 1000).toString();

	/*
	 *		Initial State for Component
	 *		Values taken from data attribute
	 */
	const initialState = () => {
		return {
			// ~need to make index name dynamic~
			[index]: {
				query: startingQuery ?? '',
				refinementList: {
					// Only load specific section(s) - as array please
					sections: config.sections ?? [],
					isSearchable: ['true'],
					requiresActiveSubscription: hasActiveSubscription
						? [true, false]
						: [false],
				},
			},
		};
	};

	/*
	 *		Set up the search instance
	 */
	const search = instantsearch({
		indexName: index,
		searchClient,
		// insights disabled for now as throws a token error
		// insights: true,
		// https://www.algolia.com/doc/guides/building-search-ui/events/js/#persist-an-anonymous-usertoken-across-sessions
		insightsInitParams: {
			useCookie: false,
		},
		initialUiState: initialState(),
		// https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-future
		future: {
			preserveSharedStateOnUnmount: true,
		},
	});

	/*
	 *		Adding hidden. predefined Widgets to Search
	 */
	// this is required for page-load filtering
	// https://discourse.algolia.com/t/how-to-set-initialuistate-refinements-in-instantsearch-js/15576
	if (node.querySelector('#AlgoliaIsSearchable')) {
		search.addWidgets([
			refinementList({
				container: '#AlgoliaIsSearchable',
				attribute: 'isSearchable',
			}),
		]);
	}

	if (node.querySelector('#AlgoliaLimitByUserStatus')) {
		search.addWidgets([
			refinementList({
				container: '#AlgoliaLimitByUserStatus',
				attribute: 'requiresActiveSubscription',
			}),
		]);
	}

	if (node.querySelector('#AlgoliaSections')) {
		search.addWidgets([
			refinementList({
				container: '#AlgoliaSections',
				attribute: 'section',
			}),
		]);
	}
	/*
	 *		Adding Widgets to Search
	 */
	// https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/
	search.addWidgets([
		configureWidget(config.resultsPerPage || 20),
		searchResultsWidget, // results
	]);

	/*
	 *		Adding Search
	 */
	const searchElement = node.querySelector('#AlgoliaSearch');
	if (searchElement) {
		search.addWidgets([searchWidget(searchElement, '', pageUrl)]);
	}

	/*
	 *		Adding Clear
	 */
	// const clearElement = node.querySelector('#AlgoliaClear');
	// if (clearElement) {
	// 	search.addWidgets([clearWidget(clearElement)]);
	// }

	/*
	 *		Add event listeners
	 */

	// Report an error to us and to the user. Handled in `app.js`
	search.on('error', ({ error }) => {
		eventBus.emit('error:algolia', { node, error });
		// throw new Error(error.message);
	});

	// kick it all off
	search.start();

	/*
	 *			 Events
	 */
	// Listen for category (section) clicks
	function onClick(event) {
		event.preventDefault();
		const {
			target,
			target: {
				dataset: { value },
			},
		} = event;

		if (target.classList.contains('is-active')) {
			target.classList.remove('is-active');
			search.helper.removeDisjunctiveFacetRefinement('section', value).search();
		} else {
			target.classList.add('is-active');
			search.helper.addDisjunctiveFacetRefinement('section', value).search();
		}
	}

	addEvents({
		'click [data-type]': onClick,
	});

	/*
	 *		If click reset; clear params
	 */
	node.addEventListener('click', ({ target }) => {
		if (target.classList.contains('ais-ClearRefinements-button')) {
			const startingUrl = window.location.origin + window.location.pathname;
			window.history.replaceState({}, '', startingUrl);
			const typeFilters = node.querySelectorAll('[data-type].is-active');
			typeFilters.forEach(f => f.classList.remove('is-active'));
		}

		// Clearing search
		if (target.classList.contains('ais-SearchBox-reset')) {
			window.history.replaceState({}, '', pageUrl);
		}
	});

	/*
	 *		Resizing
	 */
	device.at('(min-width:1024px)', {
		// Update searchBox placeholder
		on: () => {
			node.querySelector('.ais-SearchBox-input').placeholder = placeholders[0];
		},
		off: () => {
			node.querySelector('.ais-SearchBox-input').placeholder = placeholders[1];
		},
	});
};

export default withPlugins(domEvents, device)(AlgoliaSearch);
